package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import dev.lingvo.shared.localeInfo
import dev.lingvo.shared.screenshots.render.RenderReport
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.shared.screenshots.text.FitResult
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.nav.UrlState
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Text

internal data class CaptionFit(val slotKey: String, val locale: String, val report: RenderReport)

/** Compares real fitted sizes across every language and links overflowing translations to their owner. */
@Composable
internal fun FitSummary(fits: List<CaptionFit>, data: DesignPageData, expected: Int) {
    Column(Modifier.gap(Space.SM)) {
        LSectionHeading(t("app.projects.slug.screenshots.fit_summary.fit", "Caption fit"), description = t("app.projects.slug.screenshots.fit_summary.check_the_longest_captions_before_review", "Check the longest captions before review."))
        val smallest = fits.flatMap { cell -> cell.report.texts.filter { (it.primaryCaption || it.content is TextContent.SlotCaption) }.map { cell.locale to it.fit } }
            .filter { it.second !is FitResult.Empty }.minByOrNull { it.second.layout.size }
        if (smallest != null) LMuted(tf("app.projects.slug.screenshots.fit_summary.smallest", "Smallest: {size} px in {language}",
            "size" to fontPx(smallest.second.layout.size), "language" to localeInfo(smallest.first).englishName))
        if (fits.size < expected) LMuted(tf("app.projects.slug.screenshots.fit_summary.checking", "Checked {done} of {total} captions.", "done" to fits.size, "total" to expected))
        fits.filter { cell -> cell.report.texts.any { (it.primaryCaption || it.content is TextContent.SlotCaption) && it.fit is FitResult.Refused } }.forEach { cell ->
            val slot = data.plan.slots.firstOrNull { it.key == cell.slotKey }
            val bound = cell.report.texts.firstOrNull { it.primaryCaption }?.content as? TextContent.Key
            val keyId = bound?.key?.let { key -> data.captions.values.flatten().firstOrNull { it.key.name == key }?.key?.id }
                ?: slot?.captionKeyId?.takeIf { bound == null }
            LLinkButton(tf("app.projects.slug.screenshots.fit_summary.refused", "{shot} doesn't fit at the floor in {language}. Open the caption.",
                "shot" to (slot?.title ?: cell.slotKey), "language" to localeInfo(cell.locale).englishName),
                captionPath(data.project.slug, cell.locale, keyId), variant = LButtonVariant.SECONDARY,
                modifier = Modifier.styleModifier { property("white-space", "normal"); property("max-width", "100%"); property("overflow-wrap", "anywhere"); property("height", "auto"); property("min-height", "40px") })
        }
    }
}

internal fun captionPath(slug: String, locale: String, keyId: String?): String =
    "/app/projects/${encodeURIComponent(slug)}/translate/${encodeURIComponent(locale)}" + UrlState.toQuery(mapOf("key" to keyId))
private external fun encodeURIComponent(value: String): String

internal fun fontPx(value: Double): String = if (value % 1.0 == 0.0) value.toInt().toString() else value.toString()
